import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, LogOut, Pencil, X, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";

const TABS = [
  { key: "parks", label: "Parks & Refuges" },
  { key: "products", label: "Gear" },
  { key: "blog", label: "Journal" },
  { key: "analytics", label: "Analytics" },
  { key: "settings", label: "Settings" },
];

const detectRetailer = (url) => {
  if (!url) return null;
  const u = url.toLowerCase();
  if (/(^|\.)amazon\.(com|co\.uk|ca|de|fr|it|es|co\.jp|com\.au|com\.mx|in|nl|se|pl|com\.br|com\.tr|sg|ae)/.test(u) || /amzn\.to/.test(u)) return "amazon";
  if (/avantlink\.com/.test(u)) return "avantlink";
  return null;
};

const EMPTY_PARK = {
  id: null, slug: "", name: "", type: "national_park", state: "",
  lat: "", lng: "", effective_date: "", regulation_status: "lead_free_required",
  summary: "", rules_text: "", tips: "", image_url: "",
};
const EMPTY_PRODUCT = {
  id: null, name: "", category: "lures", brand: "", description: "", price: "",
  badges_text: "", image_url: "", buy_url: "", retailer: "other", featured: false,
};
const EMPTY_POST = {
  id: null, slug: "", title: "", excerpt: "", body: "", author: "EcoFisherman Editors",
  tag: "regulations", image_url: "",
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("parks");
  const [parks, setParks] = useState([]);
  const [products, setProducts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [park, setPark] = useState(EMPTY_PARK);
  const [product, setProduct] = useState(EMPTY_PRODUCT);
  const [post, setPost] = useState(EMPTY_POST);
  const [analytics, setAnalytics] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });

  useEffect(() => { if (!loading && !user) nav("/admin/login"); }, [loading, user, nav]);

  const loadAll = async () => {
    const [a, b, c] = await Promise.all([api.get("/parks"), api.get("/products"), api.get("/blog")]);
    setParks(a.data); setProducts(b.data); setPosts(c.data);
  };
  useEffect(() => { if (user) loadAll(); }, [user]);
  useEffect(() => {
    if (user && tab === "analytics") api.get("/analytics/clicks").then((r) => setAnalytics(r.data));
  }, [user, tab]);

  // --- Parks ---
  const savePark = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...park,
        lat: parseFloat(park.lat), lng: parseFloat(park.lng),
        rules: (park.rules_text || "").split("\n").map((s) => s.trim()).filter(Boolean),
      };
      delete body.rules_text; delete body.id;
      if (!body.effective_date) delete body.effective_date;
      if (park.id) {
        await api.put(`/parks/${park.id}`, body);
        toast.success("Park updated");
      } else {
        await api.post("/parks", body);
        toast.success("Park added");
      }
      setPark(EMPTY_PARK); loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const editPark = (p) => {
    setPark({
      ...EMPTY_PARK, ...p,
      rules_text: (p.rules || []).join("\n"),
      effective_date: p.effective_date || "",
      tips: p.tips || "",
      image_url: p.image_url || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const deletePark = async (id) => {
    if (!window.confirm("Delete this park?")) return;
    await api.delete(`/parks/${id}`); loadAll();
  };

  // --- Products ---
  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...product,
        badges: (product.badges_text || "").split(",").map((s) => s.trim()).filter(Boolean),
      };
      delete body.badges_text; delete body.id;
      if (product.id) {
        await api.put(`/products/${product.id}`, body);
        toast.success("Product updated");
      } else {
        await api.post("/products", body);
        toast.success("Product added");
      }
      setProduct(EMPTY_PRODUCT); loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const editProduct = (p) => {
    setProduct({
      ...EMPTY_PRODUCT, ...p,
      badges_text: (p.badges || []).join(", "),
      brand: p.brand || "", price: p.price || "", image_url: p.image_url || "",
      retailer: p.retailer || "other", featured: !!p.featured,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const deleteProduct = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/products/${id}`); loadAll();
  };

  // --- Blog ---
  const savePost = async (e) => {
    e.preventDefault();
    try {
      const body = { ...post };
      delete body.id;
      if (post.id) {
        await api.put(`/blog/${post.id}`, body);
        toast.success("Post updated");
      } else {
        await api.post("/blog", body);
        toast.success("Post added");
      }
      setPost(EMPTY_POST); loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const editPost = (p) => {
    setPost({ ...EMPTY_POST, ...p, image_url: p.image_url || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const deletePost = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/blog/${id}`); loadAll();
  };

  // --- Password ---
  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) return toast.error("New passwords don't match");
    if (pw.new_password.length < 8) return toast.error("Password must be at least 8 characters");
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password updated. Next sign-in will use it.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (loading || !user) return <div className="p-16 text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
      <div className="flex justify-between items-center mb-10">
        <div>
          <div className="label-eyebrow mb-2">Admin</div>
          <h1 className="font-display text-4xl">Content dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Signed in as {user.email}</p>
        </div>
        <button onClick={logout} className="btn-ghost" data-testid="admin-logout">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      <div className="flex gap-2 mb-8 border-b border-border overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors duration-200 whitespace-nowrap ${
              tab === t.key ? "border-[#C95B3B] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "parks" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <form onSubmit={savePark} className="space-y-3 bg-white border border-border p-6" data-testid="park-form">
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-display text-2xl flex items-center gap-2">
                {park.id ? <><Pencil className="w-5 h-5" /> Edit park</> : <><Plus className="w-5 h-5" /> Add park / refuge</>}
              </h2>
              {park.id && <button type="button" onClick={() => setPark(EMPTY_PARK)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><X className="w-3 h-3"/>Cancel</button>}
            </div>
            <Input v={park.slug} set={(v)=>setPark({...park,slug:v})} label="Slug" required />
            <Input v={park.name} set={(v)=>setPark({...park,name:v})} label="Name" required />
            <div className="grid grid-cols-2 gap-3">
              <Select v={park.type} set={(v)=>setPark({...park,type:v})} label="Type" opts={[["national_park","National Park"],["wildlife_refuge","Wildlife Refuge"],["other","Other"]]} />
              <Select v={park.regulation_status} set={(v)=>setPark({...park,regulation_status:v})} label="Status" opts={[["lead_free_required","Lead-Free Required"],["biodegradable_required","Biodegradable Required"],["proposed","Proposed"],["partial","Partial"],["no_restrictions","No Restrictions"]]} />
            </div>
            <Input v={park.state} set={(v)=>setPark({...park,state:v})} label="State" required />
            <div className="grid grid-cols-2 gap-3">
              <Input v={park.lat} set={(v)=>setPark({...park,lat:v})} label="Latitude" type="number" step="0.0001" required />
              <Input v={park.lng} set={(v)=>setPark({...park,lng:v})} label="Longitude" type="number" step="0.0001" required />
            </div>
            <Input v={park.effective_date} set={(v)=>setPark({...park,effective_date:v})} label="Effective date (YYYY-MM-DD)" />
            <Textarea v={park.summary} set={(v)=>setPark({...park,summary:v})} label="Summary" required />
            <Textarea v={park.rules_text} set={(v)=>setPark({...park,rules_text:v})} label="Rules (one per line)" rows={4} />
            <Textarea v={park.tips} set={(v)=>setPark({...park,tips:v})} label="Tips" rows={2} />
            <ImageUpload value={park.image_url} onChange={(v)=>setPark({...park,image_url:v})} label="Image (upload or paste URL)" />
            <button type="submit" className="btn-primary">{park.id ? "Save changes" : "Add park"}</button>
          </form>

          <ul className="space-y-2">
            {parks.map((p) => (
              <li key={p.id} className="flex justify-between items-center bg-white border border-border p-4" data-testid={`admin-park-${p.slug}`}>
                <div>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.state} · {p.type}</div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => editPark(p)} className="text-muted-foreground hover:text-[#C95B3B]" data-testid={`edit-park-${p.slug}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => deletePark(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === "products" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <form onSubmit={saveProduct} className="space-y-3 bg-white border border-border p-6" data-testid="product-form">
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-display text-2xl flex items-center gap-2">
                {product.id ? <><Pencil className="w-5 h-5" /> Edit product</> : <><Plus className="w-5 h-5" /> Add product</>}
              </h2>
              {product.id && <button type="button" onClick={() => setProduct(EMPTY_PRODUCT)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><X className="w-3 h-3"/>Cancel</button>}
            </div>
            <Input v={product.name} set={(v)=>setProduct({...product,name:v})} label="Name" required />
            <div className="grid grid-cols-2 gap-3">
              <Select v={product.category} set={(v)=>setProduct({...product,category:v})} label="Category" opts={[["lures","Lures"],["weights","Weights"],["line","Line"],["apparel","Apparel"],["accessories","Accessories"]]} />
              <Input v={product.brand} set={(v)=>setProduct({...product,brand:v})} label="Brand" />
            </div>
            <Textarea v={product.description} set={(v)=>setProduct({...product,description:v})} label="Description" required />
            <div className="grid grid-cols-2 gap-3">
              <Input v={product.price} set={(v)=>setProduct({...product,price:v})} label="Price" />
              <Input v={product.badges_text} set={(v)=>setProduct({...product,badges_text:v})} label="Badges (comma separated)" />
            </div>
            <ImageUpload value={product.image_url} onChange={(v)=>setProduct({...product,image_url:v})} label="Image (upload or paste URL)" />
            <Input v={product.buy_url} set={(v)=>{
              const detected = detectRetailer(v);
              setProduct({...product, buy_url:v, retailer: detected || product.retailer});
            }} label="Buy URL (raw retailer URL)" required />
            <Select v={product.retailer} set={(v)=>setProduct({...product,retailer:v})} label="Retailer (for affiliate tag)" opts={[["amazon","Amazon (auto-append ?tag=)"],["avantlink","AvantLink"],["direct","Direct brand program"],["other","Other"]]} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={product.featured} onChange={(e)=>setProduct({...product,featured:e.target.checked})} />
              Featured on home page
            </label>
            <button type="submit" className="btn-primary">{product.id ? "Save changes" : "Add product"}</button>
          </form>

          <ul className="space-y-2">
            {products.map((p) => (
              <li key={p.id} className="flex justify-between items-center bg-white border border-border p-4">
                <div>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.category} · {p.price || "no price"} · <span className={p.retailer === "amazon" ? "text-[#C95B3B] font-semibold" : ""}>{p.retailer || "other"}</span>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => editProduct(p)} className="text-muted-foreground hover:text-[#C95B3B]" data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => deleteProduct(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === "blog" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <form onSubmit={savePost} className="space-y-3 bg-white border border-border p-6" data-testid="blog-form">
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-display text-2xl flex items-center gap-2">
                {post.id ? <><Pencil className="w-5 h-5" /> Edit post</> : <><Plus className="w-5 h-5" /> Add post</>}
              </h2>
              {post.id && <button type="button" onClick={() => setPost(EMPTY_POST)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><X className="w-3 h-3"/>Cancel</button>}
            </div>
            <Input v={post.slug} set={(v)=>setPost({...post,slug:v})} label="Slug" required />
            <Input v={post.title} set={(v)=>setPost({...post,title:v})} label="Title" required />
            <Select v={post.tag} set={(v)=>setPost({...post,tag:v})} label="Tag" opts={[["regulations","Regulations"],["reviews","Reviews"],["sites","Fishing Sites"],["gear","Gear"]]} />
            <Textarea v={post.excerpt} set={(v)=>setPost({...post,excerpt:v})} label="Excerpt" required />
            <Textarea v={post.body} set={(v)=>setPost({...post,body:v})} label="Body" rows={6} required />
            <ImageUpload value={post.image_url} onChange={(v)=>setPost({...post,image_url:v})} label="Cover image (upload or paste URL)" />
            <button type="submit" className="btn-primary">{post.id ? "Save changes" : "Add post"}</button>
          </form>

          <ul className="space-y-2">
            {posts.map((p) => (
              <li key={p.id} className="flex justify-between items-center bg-white border border-border p-4">
                <div>
                  <div className="font-medium">{p.title}</div>
                  <div className="text-xs text-muted-foreground">{p.tag}</div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => editPost(p)} className="text-muted-foreground hover:text-[#C95B3B]" data-testid={`edit-post-${p.slug}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => deletePost(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === "analytics" && (
        <div className="space-y-8" data-testid="admin-analytics">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white border border-border p-6">
              <div className="label-eyebrow mb-2">Total buy clicks</div>
              <div className="font-display text-5xl">{analytics?.total_clicks ?? "—"}</div>
              <div className="text-xs text-muted-foreground mt-2">Every outbound click to a retailer, since launch.</div>
            </div>
            <div className="md:col-span-2 bg-[#E6E2D6] border border-border p-6">
              <div className="label-eyebrow mb-2 text-[#2D5A4C]">How this makes money</div>
              <p className="text-sm leading-relaxed">
                Set a product's retailer to <b>Amazon</b> and your affiliate tag (<span className="font-mono bg-white px-1">ecofisherman-20</span>)
                is auto-appended to every outbound click. Commissions show up in your Amazon Associates dashboard.
              </p>
            </div>
          </div>

          <div>
            <h3 className="font-display text-2xl mb-4">Top products by clicks</h3>
            <div className="bg-white border border-border">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F2EB] border-b border-border">
                  <tr><th className="text-left p-3 font-medium">Product</th><th className="text-left p-3 font-medium">Retailer</th><th className="text-right p-3 font-medium">Clicks</th></tr>
                </thead>
                <tbody>
                  {(analytics?.top_products || []).map((p) => (
                    <tr key={p.id} className="border-b border-border last:border-0">
                      <td className="p-3">{p.name}</td>
                      <td className="p-3 text-muted-foreground">{p.retailer || "other"}</td>
                      <td className="p-3 text-right font-mono">{p.clicks || 0}</td>
                    </tr>
                  ))}
                  {(!analytics?.top_products?.length) && <tr><td colSpan={3} className="p-6 text-center text-muted-foreground">No clicks yet. Share the site!</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="font-display text-2xl mb-4">Recent clicks</h3>
            <div className="bg-white border border-border">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F2EB] border-b border-border">
                  <tr><th className="text-left p-3 font-medium">When</th><th className="text-left p-3 font-medium">Product</th><th className="text-left p-3 font-medium">Source</th></tr>
                </thead>
                <tbody>
                  {(analytics?.recent || []).map((c, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="p-3 font-mono text-xs">{new Date(c.at).toLocaleString()}</td>
                      <td className="p-3">{c.product_name}</td>
                      <td className="p-3 text-muted-foreground">{c.source}</td>
                    </tr>
                  ))}
                  {(!analytics?.recent?.length) && <tr><td colSpan={3} className="p-6 text-center text-muted-foreground">Nothing yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div className="max-w-lg">
          <form onSubmit={changePassword} className="space-y-4 bg-white border border-border p-6" data-testid="change-password-form">
            <h2 className="font-display text-2xl flex items-center gap-2 mb-2"><KeyRound className="w-5 h-5"/> Change password</h2>
            <p className="text-sm text-muted-foreground">
              Set your own admin password before making the site public.
              You'll use the new password the next time you sign in.
            </p>
            <Input v={pw.current_password} set={(v)=>setPw({...pw,current_password:v})} label="Current password" type="password" required testid="current-password" />
            <Input v={pw.new_password} set={(v)=>setPw({...pw,new_password:v})} label="New password (8+ characters)" type="password" required testid="new-password" />
            <Input v={pw.confirm} set={(v)=>setPw({...pw,confirm:v})} label="Confirm new password" type="password" required testid="confirm-password" />
            <button type="submit" className="btn-primary" data-testid="change-password-submit">Update password</button>
          </form>
        </div>
      )}
    </div>
  );
}

const Input = ({ v, set, label, type = "text", step, required, testid }) => (
  <div>
    <label className="label-eyebrow block mb-1">{label}</label>
    <input type={type} step={step} value={v ?? ""} onChange={(e) => set(e.target.value)} required={required}
      data-testid={testid}
      className="w-full px-3 py-2 bg-[#F5F2EB] border border-border text-sm focus:outline-none focus:border-foreground" />
  </div>
);
const Textarea = ({ v, set, label, rows = 3, required }) => (
  <div>
    <label className="label-eyebrow block mb-1">{label}</label>
    <textarea value={v ?? ""} onChange={(e) => set(e.target.value)} rows={rows} required={required}
      className="w-full px-3 py-2 bg-[#F5F2EB] border border-border text-sm focus:outline-none focus:border-foreground" />
  </div>
);
const Select = ({ v, set, label, opts }) => (
  <div>
    <label className="label-eyebrow block mb-1">{label}</label>
    <select value={v} onChange={(e) => set(e.target.value)}
      className="w-full px-3 py-2 bg-[#F5F2EB] border border-border text-sm focus:outline-none focus:border-foreground">
      {opts.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
    </select>
  </div>
);
